/**
 * Dependency Review Panel - Simple Checklist UI
 *
 * Displays task dependencies in a simple checklist format (NOT drag-and-drop).
 * Shows:
 * - List of tasks with their prerequisites
 * - Dependency source (AI-suggested, heuristic, manual)
 * - Confidence scores for AI suggestions
 * - Ability to remove dependencies
 *
 * Design: Clean collapsible panel with dependency graph
 * NOTE: Phase 2.0 uses simple UI, NOT drag-and-drop dependency editor
 */

import React, { useState } from 'react';
import type { Task, Dependency } from '../types/actionPlan';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface DependencyReviewPanelProps {
  tasks: Task[];
  dependencies: Dependency[];
  /** Canonical selection (S4f-C): the view opens on this task's neighbourhood. */
  selectedTaskId?: string | null;
  onClose: () => void;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getTaskTitle = (taskId: string, tasks: Task[]): string => {
  const task = tasks.find(t => t.id === taskId);
  return task ? task.title : taskId;
};

const getSourceIcon = (source: string): string => {
  switch (source) {
    case 'ai_suggested': return '🤖';
    case 'heuristic': return '🧠';
    case 'manual': return '✏️';
    default: return '❓';
  }
};

/** The edge type as a WORD — the arrow glyph is decoration, never the encoding. */
const getTypeLabel = (type: string): string =>
  type === 'hard' ? 'must complete first' : 'should complete first';

const getSourceLabel = (source: string): string => {
  switch (source) {
    case 'ai_suggested': return 'AI Suggested';
    case 'heuristic': return 'Pattern Based';
    case 'manual': return 'Manual';
    default: return source;
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const DependencyReviewPanel: React.FC<DependencyReviewPanelProps> = ({
  tasks,
  dependencies,
  selectedTaskId = null,
  onClose
}) => {
  const [filter, setFilter] = useState<'all' | 'ai_suggested' | 'heuristic' | 'manual'>('all');

  /*
   * SELECTED-TASK NEIGHBOURHOOD (S4f-C): the view opens on the canonical
   * selection's upstream and downstream edges, so a dense 40-task chain reads
   * from the task the reader actually cares about. The full grouped list stays
   * below — the neighbourhood is a focus, not a filter.
   */
  const selectedTask = selectedTaskId
    ? tasks.find((t) => t.id === selectedTaskId) ?? null
    : null;
  const upstreamOfSelected = selectedTask
    ? dependencies.filter((d) => d.to_task_id === selectedTask.id)
    : [];
  const downstreamOfSelected = selectedTask
    ? dependencies.filter((d) => d.from_task_id === selectedTask.id)
    : [];

  // Filter dependencies
  const filteredDeps = filter === 'all'
    ? dependencies
    : dependencies.filter(d => d.source === filter);

  // Group by dependent task
  const depsByTask = new Map<string, Dependency[]>();
  filteredDeps.forEach(dep => {
    const existing = depsByTask.get(dep.to_task_id) || [];
    existing.push(dep);
    depsByTask.set(dep.to_task_id, existing);
  });

  // Count dependencies by source
  const aiCount = dependencies.filter(d => d.source === 'ai_suggested').length;
  const heuristicCount = dependencies.filter(d => d.source === 'heuristic').length;
  const manualCount = dependencies.filter(d => d.source === 'manual').length;

  return (
    <div className="dependency-review-panel">
      <div className="panel-header">
        <h3>📋 Dependency Review</h3>
        <button className="close-button" onClick={onClose}>✕</button>
      </div>

      {/* Selected-task neighbourhood (S4f-C) */}
      {selectedTask && (
        <section className="ap-dep-focus" aria-label={`Dependencies of ${selectedTask.title}`}>
          <h4 className="ap-dep-focus__title">Focused on: {selectedTask.title}</h4>
          <div className="ap-dep-focus__cols">
            <div>
              <h5>Upstream — {upstreamOfSelected.length} prerequisite{upstreamOfSelected.length === 1 ? '' : 's'}</h5>
              {upstreamOfSelected.length === 0 && <p className="ap-dep-focus__none">None recorded.</p>}
              <ul>
                {upstreamOfSelected.map((dep, i) => (
                  <li key={i}>
                    <strong>{getTaskTitle(dep.from_task_id, tasks)}</strong>{' '}
                    <span className="ap-dep-type">{getTypeLabel(dep.type)}</span>{' '}
                    <span className="ap-dep-origin">{getSourceLabel(dep.source)}
                      {dep.confidence !== undefined ? ` · ${(dep.confidence * 100).toFixed(0)}%` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5>Downstream — {downstreamOfSelected.length} dependent{downstreamOfSelected.length === 1 ? '' : 's'}</h5>
              {downstreamOfSelected.length === 0 && <p className="ap-dep-focus__none">None recorded.</p>}
              <ul>
                {downstreamOfSelected.map((dep, i) => (
                  <li key={i}>
                    <strong>{getTaskTitle(dep.to_task_id, tasks)}</strong>{' '}
                    <span className="ap-dep-type">{getTypeLabel(dep.type)}</span>{' '}
                    <span className="ap-dep-origin">{getSourceLabel(dep.source)}
                      {dep.confidence !== undefined ? ` · ${(dep.confidence * 100).toFixed(0)}%` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Summary Stats */}
      <div className="dependency-stats">
        <div className="stat-item">
          <span className="stat-label">Total Dependencies</span>
          <span className="stat-value">{dependencies.length}</span>
        </div>
        <div className="stat-item ai">
          <span className="stat-icon">🤖</span>
          <span className="stat-value">{aiCount}</span>
          <span className="stat-label">AI</span>
        </div>
        <div className="stat-item heuristic">
          <span className="stat-icon">🧠</span>
          <span className="stat-value">{heuristicCount}</span>
          <span className="stat-label">Pattern</span>
        </div>
        <div className="stat-item manual">
          <span className="stat-icon">✏️</span>
          <span className="stat-value">{manualCount}</span>
          <span className="stat-label">Manual</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({dependencies.length})
        </button>
        <button
          className={`filter-tab ${filter === 'ai_suggested' ? 'active' : ''}`}
          onClick={() => setFilter('ai_suggested')}
        >
          🤖 AI ({aiCount})
        </button>
        <button
          className={`filter-tab ${filter === 'heuristic' ? 'active' : ''}`}
          onClick={() => setFilter('heuristic')}
        >
          🧠 Pattern ({heuristicCount})
        </button>
        <button
          className={`filter-tab ${filter === 'manual' ? 'active' : ''}`}
          onClick={() => setFilter('manual')}
        >
          ✏️ Manual ({manualCount})
        </button>
      </div>

      {/* Dependency List */}
      <div className="dependency-list">
        {Array.from(depsByTask.entries()).map(([taskId, taskDeps]) => {
          const task = tasks.find(t => t.id === taskId);
          if (!task) return null;

          return (
            <div key={taskId} className="task-dependency-group">
              <h4 className="dependent-task-title">
                {task.title}
                <span className="dep-count">{taskDeps.length} prerequisites</span>
              </h4>

              <div className="dependencies">
                {taskDeps.map((dep, i) => (
                  <div key={i} className={`dependency-item ${dep.type}`}>
                    <div className="dep-left">
                      <span className="dep-icon" aria-hidden="true">{dep.type === 'hard' ? '→' : '⇢'}</span>
                      <div className="dep-content">
                        <div className="dep-title">
                          {getTaskTitle(dep.from_task_id, tasks)}{' '}
                          <span className="ap-dep-type">{getTypeLabel(dep.type)}</span>
                        </div>
                        {dep.reason && (
                          <div className="dep-reason">{dep.reason}</div>
                        )}
                      </div>
                    </div>

                    <div className="dep-right">
                      <div className="dep-source">
                        <span className="source-icon">{getSourceIcon(dep.source)}</span>
                        <span className="source-label">{getSourceLabel(dep.source)}</span>
                        {dep.confidence !== undefined && (
                          <span className="confidence-badge">
                            {(dep.confidence * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      {/*
                        * REMOVAL IS NOT OFFERED (S4f-C decision, recorded in §6A).
                        * No server contract mutates plan dependencies — no route in
                        * actionPlanRoutes touches them — and the control that stood
                        * here was a dead action (a ✕ that logged to the console).
                        * A control that pretends a capability is a fabricated claim.
                        * When a persistence contract exists, the control returns
                        * NAMED with both endpoints: "Remove dependency: <from> → <to>".
                        */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {filteredDeps.length === 0 && (
          <div className="empty-state">
            <p>No dependencies found</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="dependency-legend">
        <h4>Legend</h4>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-symbol">→</span>
            <span>Hard dependency (must complete first)</span>
          </div>
          <div className="legend-item">
            <span className="legend-symbol">⇢</span>
            <span>Soft dependency (should complete first)</span>
          </div>
          <div className="legend-item">
            <span className="legend-symbol">🤖</span>
            <span>AI-detected (Claude API)</span>
          </div>
          <div className="legend-item">
            <span className="legend-symbol">🧠</span>
            <span>Pattern-based (heuristic rules)</span>
          </div>
          <div className="legend-item">
            <span className="legend-symbol">✏️</span>
            <span>Manual (user-defined)</span>
          </div>
        </div>
      </div>
    </div>
  );
};
